import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Button from "@components/Button/Button";
import CustomDataTable from "@components/DataTable/CustomDataTable";
import type { Header } from "@components/DataTable/CustomDataTable.types";
import Subheader from "@components/Subheader/Subheader";
import { useNotification } from "@context/NotificationContext";
import { useVesselCallFormStore } from "@features/vessel-call/create/store/useVesselCallForm.store";
import PageLayout from "@layouts/PageLayout";
import { api } from "@services/api";
import { useQuery } from "@tanstack/react-query";
import { startInquiryTiming } from "@telemetry/inquiryTiming";
import { useTrackPendingInquiry } from "@telemetry/trackPendingInquiry";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { handleSingleVesselRedirect, hasAlreadyActiveVesselCall } from "./utils";
import type {
  VesselScanItem,
  VesselScanResponse,
  VesselScanResultsProps,
} from "./VesselScanResults.types";

const LANG_BASE = "vessel-call.vessel-scan.results";

const LOCAL_STORAGE_PAGE_KEY = "vessel-scan-results-page";
const LOCAL_STORAGE_PAGE_SIZE_KEY = "vessel-scan-results-page-size";
const SEARCH_INQUIRY_ID = "vessel-call.vessel-scan-results.search";
const SEARCH_INQUIRY_CONTEXT = {
  module: "vessel_call",
  screen: "Vessel Scan Results",
  action: "search_vessel_scan_results",
  entityType: "vessel",
};

const VesselScanResults: React.FC<Partial<VesselScanResultsProps>> = (props) => {
  // Removed setScannedVesselId, will use vesselId from localStorage
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { setField } = useVesselCallFormStore();
  const locationState = location.state as
    | {
        scanValue?: string;
        pagination?: { total: number; pages: number; pageSize: number; pageNumber: number };
      }
    | undefined;

  const scanValue = locationState?.scanValue;

  useEffect(() => {
    if (!scanValue) {
      navigate("/vessel-call/vessel-scan", { replace: true });
    }
  }, [scanValue, navigate]);

  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_KEY);
    return saved ? parseInt(saved, 10) : 1;
  });

  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_PAGE_SIZE_KEY);
    return saved ? parseInt(saved, 10) : 25;
  });

  // Reset to first page when scan value changes (unless already on first)
  useEffect(() => {
    if (scanValue && page !== 1) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanValue]);

  // Always call the hook, and return null if scanValue is missing
  if (!scanValue) {
    return null;
  }

  // Query (placeholderData keeps previous data while fetching new)
  const {
    data: apiData,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    dataUpdatedAt,
    error,
  } = useQuery<VesselScanResponse>({
    enabled: !!scanValue,
    queryKey: ["vessel-scan-results", scanValue, page, pageSize],
    queryFn: () =>
      api.get<VesselScanResponse>("bff-vessel", "vessels:scan", {
        params: { imoOrName: scanValue, pageNumber: page - 1, pageSize },
      }),
    placeholderData: (prev) => prev,
  });
  useTrackPendingInquiry(SEARCH_INQUIRY_ID, {
    isFetching,
    isLoading,
    isError,
    isSuccess,
    error,
    dataUpdatedAt,
  });

  // Track page / size change to know it's an intentional pagination fetch
  const pageChangeRef = useRef(false);
  useEffect(() => {
    if (isFetching) return;
    // reset flag after fetch completes
    pageChangeRef.current = false;
  }, [isFetching, apiData]);

  const vessels: VesselScanItem[] = apiData?.vessels ?? [];
  const totalCount = apiData?.totalRecordCount ?? 0;

  // Show placeholder:
  const isInitialMountLoading = isFetching && !apiData;
  const showLoadingPlaceholder = isInitialMountLoading || (isFetching && pageChangeRef.current);

  // Selecție single
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const handleSelect = useCallback((id: string | number) => setSelectedId(id), []);

  // Clear selection if selected item not present after pagination
  useEffect(() => {
    if (selectedId && !vessels.some((v) => v.id === selectedId)) {
      setSelectedId(null);
    }
  }, [vessels, selectedId]);

  const headers: Header[] = useMemo(
    () => [
      { field: "__select", columnName: "", width: 44, disableSort: true, render: () => null },
      { field: "vesselName", columnName: t(`${LANG_BASE}.name`, { defaultValue: "Vessel Name" }) },
      { field: "vesselImoNumber", columnName: t(`${LANG_BASE}.imo`, { defaultValue: "IMO No" }) },
      {
        field: "portName",
        columnName: t(`${LANG_BASE}.port`, { defaultValue: "Port of Registration" }),
      },
      {
        field: "shipBuildYear",
        columnName: t(`${LANG_BASE}.year`, { defaultValue: "Year of build" }),
      },
      { field: "countryCode", columnName: t(`${LANG_BASE}.flag`, { defaultValue: "Flag" }) },
      { field: "shipsOwner", columnName: t(`${LANG_BASE}.owner`, { defaultValue: "Ship Owner" }) },
    ],
    [t],
  );

  const rows = useMemo(
    () =>
      vessels.map((v) => ({
        ...v,
        __select: v.id,
        __isSelected: v.id === selectedId,
        __onSelect: () => handleSelect(v.id),
      })),
    [vessels, selectedId, handleSelect],
  );

  const selectedVessel = selectedId ? (vessels.find((v) => v.id === selectedId) ?? null) : null;
  const { notify } = useNotification();

  const handleProceed = async () => {
    if (!selectedVessel) {
      notify({
        title: t("vessel-call.vessel-scan.select-vessel-warning-title"),
        description: t("vessel-call.vessel-scan.select-vessel-warning-description"),
        type: "error",
      });
    } else {
      setField("vesselId", selectedVessel?.id);
      await handleSingleVesselRedirect(selectedVessel, navigate).catch((error: any) => {
        console.error("Vessel scan error:", error);
        if (hasAlreadyActiveVesselCall(error)) {
          notify({
            title: t("vessel-call.vessel-scan.already-active-error-title"),
            description: t("vessel-call.vessel-scan.already-active-error-description"),
            type: "error",
          });
          return;
        }
        notify({
          title: t("vessel-call.vessel-scan.scan-error-title"),
          description:
            error.response?.data?.detail ||
            error.message ||
            t("vessel-call.vessel-scan.scan-error-description"),
          type: "error",
        });
      });
    }
  };
  return (
    <PageLayout dir={dir}>
      <Subheader
        title={t("vessel-call.create-vessel-call")}
        subtitle={
          <span>
            <span
              onClick={() => navigate("/vessel-call")}
              className="cursor-pointer"
              style={{ color: theme.palette.text.brand }}
            >
              {t("navigation.vessel-call")}
            </span>{" "}
            / {t("vessel-call.create-vessel-call")}
          </span>
        }
      />
      <div className="pt-8 pb-8 px-24 flex flex-col gap-4">
        {error && (
          <div className="text-sm text-red-600">
            {error.message || t("common.error", { defaultValue: "Error" })}
          </div>
        )}

        {showLoadingPlaceholder ? (
          <div
            className="flex flex-col items-center justify-center py-20"
            data-testid="vessel-scan-results-loading-placeholder"
          >
            <RotateRightIcon
              fontSize="medium"
              sx={{
                "animation": "spin 0.9s linear infinite",
                "@keyframes spin": { to: { transform: "rotate(360deg)" } },
                "color": theme.palette.text.secondary,
              }}
              data-testid="vessel-scan-results-loading-spinner"
            />
          </div>
        ) : (
          <CustomDataTable
            testId={props.testId}
            title={t(`${LANG_BASE}.title`, { defaultValue: "Scan results" })}
            headers={headers}
            rows={rows}
            page={page}
            pageSize={pageSize}
            totalCount={totalCount}
            canPreview={false}
            pageSizeOptions={[15, 20, 25, 50]}
            onPageChange={(newPage, newSize) => {
              // Align behavior with ClearancesContainer: always set both values directly
              if (newPage !== page || newSize !== pageSize) {
                startInquiryTiming(SEARCH_INQUIRY_ID, SEARCH_INQUIRY_CONTEXT);
              }
              pageChangeRef.current = true;
              setPage(newPage);
              setPageSize(newSize);
              localStorage.setItem(LOCAL_STORAGE_PAGE_KEY, String(newPage));
              localStorage.setItem(LOCAL_STORAGE_PAGE_SIZE_KEY, String(newSize));
            }}
            filters={[]}
            onApplyFilters={() => {}}
            onClearFilters={() => {}}
            availableFields={{}}
            actionName=""
            navigationLinkBase=""
          />
        )}

        <Stack
          direction="row"
          spacing={4}
          sx={{ mt: 2 }}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Button
            variant="text"
            color="secondary"
            sx={{ color: theme.palette.primary.main, textTransform: "none" }}
            label={t("vessel-call.vessel-scan.scan-again")}
            onClick={() => navigate("/vessel-call/vessel-scan")}
            testId={`${props.testId}-scan-again-btn`}
          />
          <Button
            variant="contained"
            startIcon={<ContentPasteGoIcon fontSize="small" />}
            label={t("vessel-call.vessel-scan.proceed")}
            sx={{ textTransform: "none" }}
            onClick={handleProceed}
            testId={`${props.testId}-proceed-btn`}
          />
        </Stack>
      </div>
    </PageLayout>
  );
};

export default VesselScanResults;
